import { AnyAction, Reducer } from "redux";
import { IAppThunkAction } from ".";
import ParcelApi from "../api/ParcelApi";
import IAuctionStatus from "../models/auction/IAuctionStatus";
import { ICurrentBid } from "../models/parcel/ICurrentBid";
import {
    IParcelBid,
    IParcelBidDict,
    IParcelBidRequest
} from "../models/parcel/IParcelBid";
import {
    IParcelBidLog,
    IParcelBidLogState
} from "../models/parcel/IParcelBidLog";
import Constants from "../models/utility/Constants";
import {
    BidLoadState,
    LoadState,
    isCriticalErrorBidLoadState
} from "../models/utility/LoadState";
import * as Sentry from "@sentry/react";

export interface IParcelBidStoreState {
    bidDialogVisible: boolean;
    currentBid: ICurrentBid;
    parcelAdminBidLogs: IParcelBidLogState[];
    parcelBidDict: IParcelBidDict;
    parcelBidLoadState: LoadState;
}

export type KnownAction =
    | IAddParcelAdminBidLog
    | IAddParcelBid
    | IToggleBidDialogVisible
    | IToggleBidLoadState
    | IToggleParcelAdminBidLogsLoadState
    | IToggleParcelBidsDictLoadState
    | IUpdateLiveParcelBids
    | IUpdateAllParcelBids
    | IUpdateCurrentBid
    | IUpdateParcelAdminBidLogs
    | ITrackNewBid;

export interface IAddParcelAdminBidLog {
    type: "ADD_PARCEL_ADMIN_BID_LOG";
    bidLog: IParcelBidLog;
    parcelId: string;
}
export interface IAddParcelBid {
    type: "ADD_PARCEL_BID";
    bid: IParcelBid;
}
export interface IToggleBidDialogVisible {
    type: "TOGGLE_BID_DIALOG_VISIBLE";
    visible: boolean;
}
export interface IToggleBidLoadState {
    type: "TOGGLE_BID_LOAD_STATE";
    errorText: string;
    state: BidLoadState;
}
export interface IToggleParcelAdminBidLogsLoadState {
    type: "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE";
    parcelId: string;
    loadState: LoadState;
}
export interface IToggleParcelBidsDictLoadState {
    type: "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE";
    state: LoadState;
}
export interface IUpdateAllParcelBids {
    type: "UPDATE_ALL_PARCEL_BIDS";
    bids: IParcelBidDict;
}
export interface IUpdateLiveParcelBids {
    type: "UPDATE_LIVE_PARCEL_BIDS";
    auctionStatus: IAuctionStatus;
}
export interface IUpdateCurrentBid {
    type: "UPDATE_CURRENT_BID";
    currentBid: ICurrentBid;
}
export interface IUpdateParcelAdminBidLogs {
    type: "UPDATE_PARCEL_ADMIN_BID_LOGS";
    parcelId: string;
    logs: IParcelBidLog[];
}
export interface ITrackNewBid {
    type: "TRACK_NEW_BID_ID";
    bidId: string;
}

export const actionCreators = {
    loadParcelAdminBidLogs:
        (parcelId: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE",
                parcelId,
                loadState: LoadState.Loading
            });

            const logs = await ParcelApi.GetParcelBidLogs(parcelId);
            dispatch({ type: "UPDATE_PARCEL_ADMIN_BID_LOGS", parcelId, logs });
        },
    loadParcelBidsForAuction:
        (auctionId: string): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE",
                state: LoadState.Loading
            });

            const bids = await ParcelApi.GetParcelBids(auctionId);

            dispatch({
                type: "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE",
                state: LoadState.Loaded
            });
            dispatch({ type: "UPDATE_ALL_PARCEL_BIDS", bids });
        },
    resetCurrentBid: (): IAppThunkAction => (dispatch) => {
        const currentBid = {
            parcelAmount: 0,
            parcelId: "",
            parcelName: "",
            parcelToken: "",
            submitErrorText: "",
            submitLoadState: BidLoadState.NotSubmitted
        } as ICurrentBid;

        dispatch({ type: "UPDATE_CURRENT_BID", currentBid });
    },
    submitBid: (): IAppThunkAction => async (dispatch, getState) => {
        dispatch({
            type: "TOGGLE_BID_LOAD_STATE",
            errorText: "",
            state: BidLoadState.Submitting
        });

        const currentBid = getState().parcelBidStoreState.currentBid;
        const request: IParcelBidRequest = {
            checkoutToken: currentBid.parcelToken,
            parcelId: currentBid.parcelId,
            bidAmount: currentBid.parcelAmount
        };

        dispatch({ type: "SENTRY_START_PROCESS_BID", request });

        try {
            const response = await ParcelApi.SubmitParcelBid(request);

            if (response && response.errors && response.errors.length > 0) {
                const errorMsg =
                    "Errors detected while submitting the bid: " +
                    response.errors.map((err) => err.description).join(", ");
                Sentry.captureException(new Error(errorMsg), {
                    extra: { response }
                });
                dispatch({ type: "SENTRY_STOP_PROCESS_BID" });
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText: errorMsg,
                    state: BidLoadState.ErrorOther
                });
            }

            // Handle the case the bid was returned in a critical error state that we don't expect to occur
            if (
                response &&
                response.bidResponse &&
                isCriticalErrorBidLoadState(response.bidResponse.result)
            ) {
                const errorMsg =
                    BidLoadState[response.bidResponse.result] +
                    ": " +
                    response.bidResponse.errorText;
                Sentry.captureException(new Error(errorMsg), {
                    extra: { response }
                });
                dispatch({ type: "SENTRY_STOP_PROCESS_BID" });
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText: errorMsg,
                    state: response.bidResponse.result
                });
            }

            if (response && response.bidResponse) {
                dispatch({
                    type: "TRACK_NEW_BID_ID",
                    bidId: response.bidResponse.bidId
                });
            }
        } catch (e) {
            dispatch({ type: "SENTRY_STOP_PROCESS_BID" });

            if (e instanceof Error) {
                const errorText = e.message;
                if (errorText.indexOf("unauthorized") >= 0) {
                    dispatch({
                        type: "TOGGLE_BID_LOAD_STATE",
                        errorText: "Oops! Your login has expired.",
                        state: BidLoadState.ErrorLogin
                    });
                    dispatch({ type: "LOGOUT_USER" });

                    return;
                }

                Sentry.captureException(e);
                dispatch({
                    type: "TOGGLE_BID_LOAD_STATE",
                    errorText,
                    state: BidLoadState.ErrorOther
                });
            }
        }
    },
    toggleDialogVisible:
        (visible: boolean): IAppThunkAction =>
        (dispatch) => {
            dispatch({ type: "TOGGLE_BID_DIALOG_VISIBLE", visible });
        },
    toggleParcelAdminBidLogLoadState:
        (parcelId: string, loadState: LoadState): IAppThunkAction =>
        (dispatch) => {
            dispatch({
                type: "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE",
                parcelId,
                loadState
            });
        },
    updateCurrentBid:
        (
            parcelId: string,
            state: BidLoadState,
            checkoutToken = "",
            lastAcceptedBid: number | undefined = undefined,
            errorText = ""
        ): IAppThunkAction =>
        (dispatch, getState) => {
            const parcel = getState().parcelStoreState.parcels.find(
                (e) => e.parcelId === parcelId
            );
            const parcelBids =
                getState().parcelBidStoreState.parcelBidDict[parcelId] || [];

            if (!parcel) {
                return;
            }

            // ensure that initial first bid is not incremented
            const bidIncrement =
                parcelBids && parcelBids.length > 0
                    ? parcel.bidIncrement || Constants.DefaultBidIncrement
                    : 0;

            const prevAmount =
                parcel.lastAcceptedBid || parcel.startingBid || 0;
            const prevToken = parcel.checkOutToken;

            const currentBid = {
                parcelAmount: (lastAcceptedBid || prevAmount) + bidIncrement,
                parcelId: parcel.parcelId,
                parcelName: parcel.name,
                parcelToken: checkoutToken || prevToken,
                submitErrorText: errorText,
                submitLoadState: state
            } as ICurrentBid;

            dispatch({ type: "UPDATE_CURRENT_BID", currentBid });
        },
    toggleBidLoadState:
        (state: BidLoadState, errorText: string): IAppThunkAction =>
        (dispatch) => {
            dispatch({
                type: "TOGGLE_BID_LOAD_STATE",
                state: state,
                errorText: errorText
            });
        }
};

export const reducer: Reducer<IParcelBidStoreState> = (
    state: IParcelBidStoreState | undefined,
    incomingAction: AnyAction
) => {
    const action = incomingAction as KnownAction;
    const prevState = state || defaultState;

    switch (action.type) {
        case "ADD_PARCEL_ADMIN_BID_LOG": {
            const parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    logs: [action.bidLog, ...e.logs]
                };
            });

            return {
                ...prevState,
                parcelAdminBidLogs
            };
        }
        case "ADD_PARCEL_BID": {
            const parcelBidDict = {
                ...prevState.parcelBidDict
            };

            const arr = parcelBidDict[action.bid.parcelId] || [];
            parcelBidDict[action.bid.parcelId] = [...arr, action.bid];

            return {
                ...prevState,
                parcelBidDict
            };
        }
        case "TOGGLE_BID_DIALOG_VISIBLE": {
            return {
                ...prevState,
                bidDialogVisible: action.visible
            };
        }
        case "TOGGLE_BID_LOAD_STATE": {
            return {
                ...prevState,
                currentBid: {
                    ...prevState.currentBid,
                    submitErrorText: action.errorText,
                    submitLoadState: action.state
                }
            };
        }
        case "TRACK_NEW_BID_ID": {
            return {
                ...prevState,
                currentBid: {
                    ...prevState.currentBid,
                    bidId: action.bidId
                }
            };
        }
        case "TOGGLE_PARCEL_ADMIN_BID_LOGS_LOAD_STATE": {
            let parcelAdminBidLogs = [...prevState.parcelAdminBidLogs];

            const oldStateIdx = prevState.parcelAdminBidLogs.findIndex(
                (e) => e.parcelId === action.parcelId
            );
            const newState = {
                loadState: action.loadState,
                logs: [],
                parcelId: action.parcelId
            } as IParcelBidLogState;

            if (oldStateIdx > -1) {
                newState.logs = [
                    ...prevState.parcelAdminBidLogs[oldStateIdx].logs
                ];

                parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                    if (e.parcelId !== action.parcelId) {
                        return e;
                    }

                    return newState;
                });
            } else {
                parcelAdminBidLogs.push(newState);
            }

            return {
                ...prevState,
                parcelAdminBidLogs
            };
        }
        case "TOGGLE_PARCEL_BIDS_DICT_LOAD_STATE": {
            return {
                ...prevState,
                parcelBidLoadState: action.state
            };
        }
        case "UPDATE_ALL_PARCEL_BIDS": {
            return {
                ...prevState,
                parcelBidDict: action.bids
            };
        }
        case "UPDATE_LIVE_PARCEL_BIDS": {
            const parcelBidDict = {
                ...prevState.parcelBidDict
            };

            const currentParcelBids =
                prevState.parcelBidDict &&
                prevState.parcelBidDict[action.auctionStatus.parcelId]
                    ? prevState.parcelBidDict[action.auctionStatus.parcelId]
                    : [];
            const responseParcelBids =
                action.auctionStatus.bids &&
                action.auctionStatus.bids[action.auctionStatus.parcelId]
                    ? action.auctionStatus.bids[action.auctionStatus.parcelId]
                    : [];
            const latestParcelBids =
                currentParcelBids &&
                responseParcelBids &&
                currentParcelBids.length !== responseParcelBids.length
                    ? responseParcelBids
                    : currentParcelBids;

            parcelBidDict[action.auctionStatus.parcelId] = [
                ...latestParcelBids
            ];

            const parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                if (e.parcelId !== action.auctionStatus.parcelId) {
                    return e;
                }

                return {
                    loadState: LoadState.Loaded,
                    logs: action.auctionStatus.bidLogs,
                    parcelId: action.auctionStatus.parcelId
                } as IParcelBidLogState;
            });

            return {
                ...prevState,
                parcelAdminBidLogs,
                parcelBidDict
            };
        }
        case "UPDATE_CURRENT_BID": {
            return {
                ...prevState,
                currentBid: action.currentBid
            };
        }
        case "UPDATE_PARCEL_ADMIN_BID_LOGS": {
            // update state in array
            const parcelAdminBidLogs = prevState.parcelAdminBidLogs.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    loadState: LoadState.Loaded,
                    logs: action.logs,
                    parcelId: action.parcelId
                } as IParcelBidLogState;
            });

            return {
                ...prevState,
                parcelAdminBidLogs
            };
        }
        default:
            break;
    }

    return prevState;
};

export const defaultState = {
    bidDialogVisible: false,
    currentBid: {} as ICurrentBid,
    parcelAdminBidLogs: [],
    parcelBidDict: {},
    parcelBidLoadState: LoadState.NotLoaded
} as IParcelBidStoreState;
